import {
  Box,
  Button,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FaEdit } from "react-icons/fa";
import moment from "moment";
import ReactModal from "../../../../Components/Modal/Modal";
import Input from "../../../../Components/Input/Input";
import { useStyle } from "../LeaveManagement/leaveStyles";
import { useStyle as useDepartmentStyles } from "../HRMSetup/Departments/department-styles";
import { BaseURL } from "../../../../Utils/interceptors";
import { getToken } from "../../../../Utils/helper";
import axios from "axios";
import { toast } from "react-toastify";
import {
  formatAmountInputDisplay,
  parseAmountNumber,
  preventInvalidAmountKeys,
} from "../../../../Utils/helper";

const amountInputProps = {
  inputMode: "decimal",
  onKeyDown: preventInvalidAmountKeys,
};

const formatRs = (value) =>
  `Rs ${Number(value || 0).toLocaleString("en-PK", { maximumFractionDigits: 2 })}`;

const getPaymentRowsWithAmountLeft = (loan) => {
  const totalPayable =
    Number(loan?.total_payable) ||
    (Number(loan?.amount) || 0) + (Number(loan?.service_charge) || 0);
  const sorted = [...(loan?.payments || [])].sort(
    (a, b) => new Date(a.month) - new Date(b.month),
  );
  let cumulative = 0;
  return sorted.map((payment) => {
    cumulative += Number(payment.amount) || 0;
    return {
      ...payment,
      amount_left: Math.max(0, totalPayable - cumulative),
    };
  });
};

const LoanPaymentHistory = ({ loan, isHr, onUpdated }) => {
  const classes = useStyle();
  const deptClasses = useDepartmentStyles();
  const [showEditModal, setShowEditModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentMonth, setEditPaymentMonth] = useState("");
  const [editPaymentAmount, setEditPaymentAmount] = useState("");

  if (!loan || loan.hr_status !== "approved") {
    return null;
  }

  const apiOptions = () => ({
    headers: {
      "Content-Type": "application/json",
      ...(getToken() && { Authorization: `Bearer ${getToken()}` }),
    },
  });

  const openEditPaymentModal = (payment) => {
    setEditingPayment(payment);
    setEditPaymentMonth(moment(payment.month).format("YYYY-MM"));
    setEditPaymentAmount(formatAmountInputDisplay(String(payment.amount)));
    setShowEditModal(true);
  };

  const closeEditPaymentModal = () => {
    setShowEditModal(false);
    setEditingPayment(null);
    setEditPaymentMonth("");
    setEditPaymentAmount("");
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment) {
      return;
    }
    if (!editPaymentMonth || !editPaymentAmount) {
      toast.error("Please select month and enter payment amount");
      return;
    }
    try {
      setPaymentLoading(true);
      const res = await axios.put(
        `${BaseURL}/loan-request/${loan._id}/payment/${editingPayment._id}`,
        {
          month: editPaymentMonth,
          amount: parseAmountNumber(editPaymentAmount),
        },
        apiOptions(),
      );
      toast.success(res?.data?.message);
      closeEditPaymentModal();
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  const paymentRows = getPaymentRowsWithAmountLeft(loan);

  return (
    <Row>
      <Col md={12}>
        <ReactModal
          open={showEditModal}
          setOpen={(open) => {
            if (!open) closeEditPaymentModal();
          }}
          title="Edit Payment"
        >
          <Box sx={{ my: 2 }}>
            <Input>
              <Form.Label>Month/Year</Form.Label>
              <Form.Control
                type="month"
                value={editPaymentMonth}
                onChange={(e) => setEditPaymentMonth(e.target.value)}
              />
            </Input>
          </Box>
          <Box sx={{ my: 2 }}>
            <InputLabel className={deptClasses.department_label}>
              Amount (Rs)
            </InputLabel>
            <TextField
              fullWidth
              className={deptClasses.inputContainer}
              type="text"
              value={editPaymentAmount}
              inputProps={amountInputProps}
              onChange={(e) =>
                setEditPaymentAmount(formatAmountInputDisplay(e.target.value))
              }
              sx={{ mt: 1 }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                variant="contained"
                className={deptClasses.cancelBtn}
                onClick={closeEditPaymentModal}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className={deptClasses.createBtn}
                onClick={handleUpdatePayment}
                disabled={paymentLoading}
              >
                {paymentLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Box>
        </ReactModal>

        <Box className="my-2 mx-4">
          <Paper className={classes.sectionsContainer}>
            <Box className={classes.sectionHeader}>Payment History</Box>
            <Box sx={{ p: 2 }}>
              {paymentRows.length === 0 ? (
                <Typography variant="h6" className="mt-1">
                  No payments recorded yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="center">Amount Paid</TableCell>
                      <TableCell align="center">Amount Left</TableCell>
                      <TableCell align="center">Recorded On</TableCell>
                      {isHr && <TableCell align="center">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentRows.map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell>
                          {moment(payment.month).format("MMMM YYYY")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "success.main" }}>
                          {formatRs(payment.amount)}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "error.main" }}>
                          {formatRs(payment.amount_left)}
                        </TableCell>
                        <TableCell align="center">
                          {moment(payment.createdAt).format("DD-MM-YYYY")}
                        </TableCell>
                        {isHr && (
                          <TableCell align="center">
                            <Box
                              className="leaves_management_btn_container"
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <button
                                type="button"
                                className="edit_btn"
                                onClick={() => openEditPaymentModal(payment)}
                                title="Edit payment"
                              >
                                <FaEdit />
                              </button>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Paper>
        </Box>
      </Col>
    </Row>
  );
};

export default LoanPaymentHistory;
